import { useModalFocusTrap } from "../../hooks/useModalFocusTrap";
import {
  ACCENT,
  BRAND,
  INTERACTION
} from "./shared/styles";

const SDX_FONT = "TheSans, sans-serif";
const SDX_HEADING = BRAND;
const SDX_BODY = "var(--sdx-text-color-body-default, #3b3a3f)";
const SDX_CAPTION = "var(--sdx-text-color-body-caption, #3b3a3f)";
const SDX_BORDER_MUTED = "var(--sdx-border-color-ui-inverse, #dbdae1)";
const SDX_SUCCESS = "var(--sdx-text-color-success-emphasis, #41825d)";
const SDX_RADIUS_L = "var(--sdx-border-radius-large, 16px)";
const SDX_SHADOW = "var(--sdx-box-shadow-default, 0 4px 16px 0 rgba(0, 0, 0, 0.16))";
const SDX_SHADOW_HOVER = "var(--sdx-box-shadow-hover, 0 4px 24px 0 rgba(0, 0, 0, 0.08))";

const PLANS = [
  {
    key: "free",
    title: "FREE",
    subtitle: "Standortbestimmung",
    price: "CHF 0",
    availability: "Ab sofort",
    features: [
      { icon: "icon-check-mark", text: "Vollständige Datenerfassung" },
      { icon: "icon-check-mark", text: "CO₂-Bilanz" },
      { icon: "icon-check-mark", text: "Scoring (Energieeffizienz & CO₂)" },
      { icon: "icon-check-mark", text: "Import von Excel-Daten, z. B. Carbonpulse" }
    ],
    disabled: true,
    current: true,
    ctaLabel: "Aktuelle Version"
  },
  {
    key: "basic",
    title: "BASIC",
    subtitle: "Reduktionsplan",
    price: "Ab CHF 1'400 pro Jahr",
    availability: "Q1 2027",
    features: [
      { icon: "icon-plus", text: "Konkrete Massnahmen (CO₂ + Kosten)" },
      { icon: "icon-plus", text: "Förder-Hinweis «Fördergelder möglich» bei Massnahmen" },
      { icon: "icon-plus", text: "Mehrjahres-Projektion und Kosten mit Zielpfad" }
    ],
    disabled: true,
    ctaLabel: "Bald verfügbar"
  },
  {
    key: "pro",
    title: "PRO",
    subtitle: "Automatisierung & Branchenvergleich",
    price: "CHF -",
    availability: "Ab Ende Q2 2027",
    badge: "EMPFOHLEN",
    features: [
      { icon: "icon-plus", text: "Scoring/Benchmarking (Energie, CO₂ & Reparaturquoten)" },
      { icon: "icon-plus", text: "Anbindung an Softwarelösungen – validierte Daten" },
      { icon: "icon-plus", text: "Offerten von Anbietern von Reduktionslösungen einholen (Marktplatz)" },
      { icon: "icon-plus", text: "Automatisiert Fördermittel beantragen" },
      { icon: "icon-plus", text: "CO₂-/Nachhaltigkeits-Report" }
    ],
    highlighted: true,
    disabled: true,
    ctaLabel: "Bald verfügbar"
  }
];

function PricingCard({ plan }) {
  const isDisabled = plan.disabled;
  const isHighlighted = plan.highlighted;
  const isCurrent = plan.current;

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 0",
        minWidth: 270,
        background: isHighlighted
          ? "linear-gradient(180deg, rgba(69, 231, 226, 0.10), #fff 45%)"
          : "#fff",
        border: isHighlighted
          ? `2px solid ${ACCENT}`
          : `1px solid ${SDX_BORDER_MUTED}`,
        borderRadius: SDX_RADIUS_L,
        padding: "2.75rem 1.85rem 1.85rem",
        boxShadow: isHighlighted
          ? "0 16px 36px rgba(69, 231, 226, 0.18)"
          : SDX_SHADOW_HOVER,
        opacity: isDisabled && !isCurrent ? 0.7 : 1,
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        fontFamily: SDX_FONT
      }}
    >
      {plan.badge && (
        <span
          style={{
            position: "absolute",
            top: -15,
            left: "50%",
            transform: "translateX(-50%)",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.35rem",
            background: ACCENT,
            color: BRAND,
            font: "var(--sdx-font-semibold-body-xs, 600 12px/18px TheSans, sans-serif)",
            letterSpacing: "0.04em",
            padding: "0.4rem 1rem",
            borderRadius: 999,
            whiteSpace: "nowrap",
            boxShadow: "0 6px 14px rgba(69, 231, 226, 0.28)"
          }}
        >
          <sdx-icon icon-name="icon-star" icon-size="1" style={{ "--sdx-icon-color": BRAND }}></sdx-icon>
          {plan.badge}
        </span>
      )}

      <h3
        style={{
          margin: 0,
          font: "var(--sdx-font-bold-heading-s, 700 20px/28px TheSans, sans-serif)",
          letterSpacing: "0.04em",
          color: SDX_HEADING
        }}
      >
        {plan.title}
      </h3>
      <p
        style={{
          margin: "0.35rem 0 1.25rem",
          fontStyle: "italic",
          color: SDX_CAPTION,
          font: "var(--sdx-font-semilight-body-s, 400 14px/20px TheSans, sans-serif)"
        }}
      >
        {plan.subtitle}
      </p>

      <div
        style={{
          font: "var(--sdx-font-bold-heading-m, 700 24px/32px TheSans, sans-serif)",
          color: INTERACTION
        }}
      >
        {plan.price}
      </div>
      <p
        style={{
          margin: "0.5rem 0 0",
          color: SDX_CAPTION,
          font: "var(--sdx-font-semilight-body-s, 400 14px/20px TheSans, sans-serif)"
        }}
      >
        Verfügbarkeit: {plan.availability}
      </p>

      <div
        style={{
          height: 2,
          background: `linear-gradient(90deg, ${ACCENT}, transparent)`,
          margin: "1.35rem 0"
        }}
      />

      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "0.75rem", flex: 1 }}>
        {plan.features.map((feature, index) => (
          <li
            key={index}
            style={{
              display: "flex",
              gap: "0.6rem",
              alignItems: "flex-start",
              font: "var(--sdx-font-semilight-body-s, 400 14px/20px TheSans, sans-serif)",
              color: SDX_BODY
            }}
          >
            <sdx-icon
              icon-name={feature.icon}
              icon-size="2"
              style={{ "--sdx-icon-color": feature.icon === "icon-arrow-right" ? SDX_CAPTION : SDX_SUCCESS, flex: "none", marginTop: 1 }}
            ></sdx-icon>
            <span>{feature.text}</span>
          </li>
        ))}
      </ul>

      <sdx-button
        variant={isDisabled ? undefined : "primary"}
        theme={isDisabled ? "secondary" : undefined}
        label={plan.ctaLabel}
        disabled={isDisabled}
        icon-name={isDisabled && !isCurrent ? "icon-lock" : undefined}
        style={{ marginTop: "1.75rem", width: "100%" }}
      ></sdx-button>
    </div>
  );
}

export default function PricingModal({ isOpen, onClose }) {
  const dialogRef = useModalFocusTrap(isOpen, onClose);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 17, 85, 0.45)",
        zIndex: 99999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem",
        overflowY: "auto",
        fontFamily: SDX_FONT
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pricing-modal-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 1100,
          maxHeight: "92vh",
          overflowY: "auto",
          background: "#fff",
          borderRadius: SDX_RADIUS_L,
          boxShadow: SDX_SHADOW,
          padding: "2rem"
        }}
      >
        <sdx-button
          theme="secondary"
          label="Zurück zum Dashboard"
          icon-name="icon-arrow-left"
          onClick={onClose}
          style={{ marginBottom: "1.5rem" }}
        ></sdx-button>

        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <h2
            id="pricing-modal-title"
            style={{
              margin: 0,
              font: "var(--sdx-font-bold-heading-l, 700 28px/32px TheSans, sans-serif)",
              color: SDX_HEADING
            }}
          >
            Passende Stufe wählen
          </h2>
          <p
            style={{
              margin: "0.5rem 0 0",
              color: SDX_CAPTION,
              font: "var(--sdx-font-semilight-body-base, 400 16px/24px TheSans, sans-serif)"
            }}
          >
            Basic und Pro sind in Entwicklung und aktuell noch nicht buchbar.
          </p>
        </div>

        <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", alignItems: "stretch", justifyContent: "center" }}>
          {PLANS.map((plan) => (
            <PricingCard key={plan.key} plan={plan} />
          ))}
        </div>
      </div>
    </div>
  );
}
